// import React, { useState } from "react";
import React, { useState, useMemo } from "react";
import { Box, Typography, useTheme, Dialog, DialogContent, Paper } from "@mui/material";
import SidebarLayout from "../../../shared/sections/sidebar/SidebarLayout";
import TitleSection from "../../../shared/sections/TitleSection";
import LeftNavigation from "../../../shared/sections/NavigationSection";
import InfoNotification from "../../../shared/modules/InfoNotification";
import QuickCostButton from "../../../shared/modules/QuickCostButton";
import CircularLoading from "../../../shared/modules/CircularLoading";
import ProgressiveAnalysisTracker from "./ProgressiveAnalysisTracker";
import FormulationTable from "../../../shared/modules/FormulationTable";
import ResultsSection from "../../../shared/sections/ResultsSection";
import { CastingQuestions } from "./questions";
import { CastingPlayBook } from "./playbook";
import DescriptionIcon from "@mui/icons-material/Description";
import ScienceIcon from "@mui/icons-material/Science";
import TuneIcon from "@mui/icons-material/Tune";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { apiFetch } from "../../../api/apiClient";
import { useAuth } from "../../../context/AuthContext";

const DEFAULT_BREAKDOWN_ORDER = ["material", "process",  "ohp"];
const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "total", "logistics"]);
const BREAKDOWN_LABELS = {
  material: "Material",
  process: "Process",
  ohp: "Overhead & Profits",
};
 
const toLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
 
const buildCostRows = (breakdown = {}) => {
  const entries = Object.entries(breakdown).filter(([key, value]) => {
    // const normKey = normalizeKey(key);
    // if (SKIP_BREAKDOWN_KEYS.has(normKey)) return false;
    if (SKIP_BREAKDOWN_KEYS.has(key)) return false;
    // if (ALLOWED_BREAKDOWN_KEYS.size > 0 && !ALLOWED_BREAKDOWN_KEYS.has(normKey)) return false;
    return Number.isFinite(Number(value));
  });
 
  entries.sort((a, b) => {
    // const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(a[0]));
    // const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(b[0]));
    const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === a[0]);
    const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === b[0]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });
 
  return entries.map(([key, value]) => {
    // const normKey = normalizeKey(key);
    // const label = BREAKDOWN_LABELS[key] || BREAKDOWN_LABELS[normKey] || toLabel(key);
    const label = BREAKDOWN_LABELS[key] || toLabel(key);
    return {
      key,
      label,
      value: Number(value) || 0,
    };
  });
};

const normalizeCastingRiskData = (riskData) => {
  if (!riskData || typeof riskData !== "object") {
    return riskData;
  }

  const fallbackDetailByTitle = {
    "Material Supply Disruption": "Supplier or alloy shortages can delay melt schedules and shipment dates.",
    "Fluctuation in Material Costs": "Alloy price swings can raise unit cost before quotes are refreshed.",
    "Process Efficiency Challenges": "Yield loss or longer cycles can increase conversion cost quickly.",
    "Shipping Delays": "Freight or dispatch delays can push customer delivery commitments.",
    "Regulatory Compliance": "Spec or compliance changes can trigger rework, retesting, or release delays.",
    "Alloy Cost Volatility": "Alloy index movement can erode margin if supplier rates are not locked.",
    "Process Yield Variability": "Scrap and rework variation can reduce output and raise conversion cost.",
    "Foundry Capacity Constraints": "Capacity bottlenecks can extend lead times during peak demand periods.",
    "Quality Drift": "Porosity or dimensional drift can increase inspection, rejection, and rework.",
    "Energy Cost Exposure": "Power and furnace cost increases can lift process cost unexpectedly.",
  };

  const normalizedRiskItems = Array.isArray(riskData.risk_items)
    ? riskData.risk_items.map((item) => {
        if (!item || typeof item !== "object") {
          return item;
        }

        const title = item.title || item.area || item.ingredient || "Risk";
        const existingDetail =
          item.description || item.detail || item.risk || item.impact_note || "";
        const meaningfulDetail =
          String(existingDetail).trim() || fallbackDetailByTitle[title] || "Monitor this risk and take corrective action early.";

        return {
          ...item,
          title,
          detail: meaningfulDetail,
          description: meaningfulDetail,
        };
      })
    : riskData.risk_items;

  return {
    ...riskData,
    risk_items: normalizedRiskItems,
  };
};

const normalizeCastingSubstituteData = (substituteData) => {
  if (!substituteData || typeof substituteData !== "object") {
    return substituteData;
  }

  const ideas = Array.isArray(substituteData.substitutes)
    ? substituteData.substitutes
    : Array.isArray(substituteData.ideas)
      ? substituteData.ideas
      : [];

  let prioritization = Array.isArray(substituteData.prioritization)
    ? substituteData.prioritization
        .map((item) => String(item || "").trim())
        .filter((item) => item && !["1", "2", "3"].includes(item))
    : [];

  if (prioritization.length === 0) {
    prioritization = ideas.slice(0, 3).map((idea, index) => {
      const proposedItem = idea?.proposed_item || idea?.substitute || "the proposed change";
      const riskNote = idea?.risk_note || "validation scope remains manageable";

      if (index === 0) {
        return `Start with ${proposedItem.toLowerCase()} because it offers the quickest savings with manageable validation effort.`;
      }
      if (index === 1) {
        return `Next, evaluate ${proposedItem.toLowerCase()} to improve yield or throughput once the first change is stable.`;
      }
      return `Then consider ${proposedItem.toLowerCase()} after confirming quality, since ${riskNote.toLowerCase()}.`;
    });
  }

  return {
    ...substituteData,
    prioritization,
  };
};



export default function CastingPage() {
  const { user } = useAuth();
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
  };
  const perlSpacing = theme.custom?.perlSpacing || {};

  const [activeSection, setActiveSection] = useState("product");
  const [sectionStarted, setSectionStarted] = useState({
    product: false,
    formulation: false,
    playbook: false,
    results: false,
  });

  const [productData, setProductData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [recipeData, setRecipeData] = useState([]);
  const [playBookData, setPlayBookData] = useState(null);
  const [oldPlayBookData, setOldPlayBookData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [substituteData, setSubstituteData] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [keyInsightsData, setKeyInsightsData] = useState(null);
  const [demoMode, setDemoMode] = useState(false);

  // Progressive analysis tracker state
    const [showProgressTracker, setShowProgressTracker] = useState(false);
    const [progressSteps, setProgressSteps] = useState([]);
    const [currentProgressStep, setCurrentProgressStep] = useState(0);
    const [overallProgress, setOverallProgress] = useState(0);

  // Functional groups for formulation
  // const functionalGroups = [
  //   "Bulk ingredient",
  //   "Sweetners",
  //   "Oil and fats",
  //   "Active ingredient",
  //   "Leavening agents",
  //   "Flavour enhancers",
  //   "Flavours",
  //   "Colours",
  //   "Preservatives",
  //   "Emulsifiers",
  //   "Stabilizers",
  //   "Thickeners",
  //   "Acidulants",
  //   "Vitamins and minerals",
  //   "Other",
  // ];
  const handleStartSection = () => {
    setSectionStarted((prev) => ({ ...prev, [activeSection]: true }));
  };

  const handleProductComplete = (data) => {
    setProductData(data);
  };

  const handleDemoModeChange = (isDemoMode) => {
    setDemoMode(isDemoMode);
  };

  const handleProductReset = () => {
    setActiveSection("product");
    setProductData(null);
    setPlayBookData(null);
    setCostData(null);
    setRiskData(null);
    setSubstituteData(null);
    setProductImageUrl(null);
    setGeneratingImage(false);
    setKeyInsightsData(null);
    setSectionStarted((prev) => ({
      ...prev,
      playbook: false,
      results: false,
    }));
  };

  // Helper function to recalculate OHP cost when OHP Rate changes
  // const recalculateOHPCost = (newOhpRate, costData, updatedCostParams) => {
  //   if (!costData || !costData.breakdown || !costData._originalResult) {
  //     return costData;
  //   }

  //   const to4 = (n) => {
  //     if (n === null || n === undefined || n === '') return 0;
  //     if (typeof n === 'string') {
  //       const cleaned = n.replace(/[$,\s]/g, '').trim();
  //       const parsed = parseFloat(cleaned);
  //       return isNaN(parsed) ? 0 : Number(Number(parsed).toFixed(4));
  //     }
  //     return Number(Number(n).toFixed(4));
  //   };

  //   // Get base material and process costs (these don't change)
  //   const material = Number(costData.breakdown.material || 0);
  //   const process = Number(costData.breakdown.process || 0);
  //   const baseOhpCost = Number(costData.meta?.baseOhpCost ?? costData.breakdown.ohp ?? 0);
  //   const baseOhpRate =
  //     Number(costData.meta?.baseOhpRate) ||
  //     (material + process > 0 ? (baseOhpCost / (material + process)) * 100 : 0);
  //   const baseFormulaCalc =
  //     material + process > 0 ? (material + process) * (baseOhpRate / 100) : 0;
  //   console.log(
  //     "🧮 Base OHP Formula:",
  //     `(${material} + ${process}) × (${baseOhpRate}/100) = ${baseFormulaCalc}`
  //   );

  //   // Recalculate OHP based on percentage change relative to original rate
  //   const targetOhpRate =
  //     typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate ?? 0);
  //   console.log(
  //     "➡️ Recalculating OHP:",
  //     `Current meta rate=${costData.meta?.currentOhpRate}, base rate=${baseOhpRate}`
  //   );

  //   let ohp = baseOhpCost;
  //   if (!isNaN(targetOhpRate)) {
  //     if (baseOhpRate > 0) {
  //       const rateRatio = targetOhpRate / baseOhpRate;
  //       const calc = baseOhpCost * rateRatio;
  //       console.log(
  //         "📐 OHP (based on ratio):",
  //         `(${baseOhpCost} * ${rateRatio}) = ${calc}`
  //       );
  //       ohp = to4(calc);
  //       } else {
  //       const ohpRateDecimal =
  //         targetOhpRate > 1 ? targetOhpRate / 100 : targetOhpRate;
  //       const calc = (material + process) * ohpRateDecimal;
  //       console.log(
  //         "📐 OHP (Material+Process * rate):",
  //         `(${material} + ${process}) * ${ohpRateDecimal} = ${calc}`
  //       );
  //       ohp = to4(calc);
  //     }
  //     const targetFormulaCalc =
  //       (material + process) * (targetOhpRate / 100);
  //     console.log(
  //       "🧮 Target OHP Formula:",
  //       `(${material} + ${process}) × (${targetOhpRate}/100) = ${targetFormulaCalc}`
  //     );
  //   }

  //   // Calculate new total: material + process + ohp
  //   const calculatedTotal = Number(material) + Number(process) + Number(ohp);
  //   const total = to4(calculatedTotal);

  //   // Update assumptions array with updated PlayBook values
  //   const resultData = costData._originalResult;
  //   const assumptionsArray = [];
    
  //   // Part Dimensions - from product data
  //   const dimensionData = productData?.partWeightDimensions || {};
  //   if (dimensionData.partLength && dimensionData.partWidth && dimensionData.partHeight && dimensionData.finishWeight) {
  //     assumptionsArray.push(
  //       `Part dimensions: L: ${dimensionData.partLength}mm, W: ${dimensionData.partWidth}mm, H: ${dimensionData.partHeight}mm, Weight: ${dimensionData.finishWeight}g`
  //     );
  //   }
    
  //   // Machine Tonnage
  //   if (updatedCostParams.machineTonnage) {
  //     assumptionsArray.push(`Machine tonnage: ${updatedCostParams.machineTonnage}`);
  //   } else if (resultData.Tonnage || resultData.cTonnage) {
  //     assumptionsArray.push(`Machine tonnage: ${resultData.Tonnage || resultData.cTonnage}`);
  //   }
    
  //   // Cavities
  //   if (updatedCostParams.cavities) {
  //     assumptionsArray.push(`Cavities: ${updatedCostParams.cavities}`);
  //   } else if (resultData.cCavities || resultData.Cavities) {
  //     assumptionsArray.push(`Cavities: ${resultData.cCavities || resultData.Cavities}`);
  //   }
    
  //   // Runner Type
  //   if (updatedCostParams.runnerType && updatedCostParams.runnerType !== "N/A") {
  //     assumptionsArray.push(`Runner type: ${updatedCostParams.runnerType}`);
  //   } else if (resultData.RunnerType) {
  //     assumptionsArray.push(`Runner type: ${resultData.RunnerType}`);
  //   }
    
  //   // Cycle Time
  //   if (updatedCostParams.cycleTime) {
  //     assumptionsArray.push(`Cycle time: ${updatedCostParams.cycleTime}s`);
  //   } else if (resultData.CycleTime) {
  //     assumptionsArray.push(`Cycle time: ${resultData.CycleTime}s`);
  //   }
    
  //   // Machine Rate - always from stored procedure
  //   if (resultData.MHR) {
  //     const machineRate = typeof resultData.MHR === 'number' ? resultData.MHR : parseFloat(resultData.MHR);
  //     if (!isNaN(machineRate)) {
  //       assumptionsArray.push(`Machine rate: $${machineRate.toFixed(4)}/hour`);
  //     }
  //   }
    
  //   // Material Price - always from stored procedure
  //   if (resultData.RMPrice) {
  //     const materialPrice = typeof resultData.RMPrice === 'number' ? resultData.RMPrice : parseFloat(resultData.RMPrice);
  //     if (!isNaN(materialPrice)) {
  //       assumptionsArray.push(`Material price: $${materialPrice.toFixed(4)}/kg`);
  //     }
  //   }
    
  //   // Labour Cost - always from stored procedure
  //   if (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) {
  //     const labourCost = typeof (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) === 'number' 
  //       ? (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost)
  //       : parseFloat(resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost);
  //     if (!isNaN(labourCost)) {
  //       assumptionsArray.push(`Labour cost: $${labourCost.toFixed(4)}/unit`);
  //     }
  //   }
    
  //   // OHP Rate - use updated value
  //   if (newOhpRate !== null && newOhpRate !== undefined && newOhpRate !== 0) {
  //     const ohpRateValue = typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate);
  //     if (!isNaN(ohpRateValue)) {
  //       assumptionsArray.push(`OHP rate: ${ohpRateValue.toFixed(2)}%`);
  //     }
  //   }

  //   // Update cost data with new OHP, total, and assumptions
  //   return {
  //     ...costData,
  //     breakdown: {
  //       ...costData.breakdown,
  //       ohp: ohp,
  //       total_should_cost: total,
  //     },
  //     summary: costData.summary.replace(
  //       /\$[\d.]+ per unit/,
  //       `$${total} per unit`
  //     ),
  //     assumptions:
  //       assumptionsArray.length > 0 ? assumptionsArray : costData.assumptions,
  //     meta: {
  //       ...costData.meta,
  //       currentOhpRate: targetOhpRate,
  //     },
  //   };
  // };

  // // Helper function to fetch material price and update material cost when tonnage changes
  // const updateMaterialCostFromTonnage = async (tonnage, updatedCostParams) => {
  //   console.log("\n" + "=".repeat(80));
  //   console.log("💰 updateMaterialCostFromTonnage called");
  //   console.log("=".repeat(80));
  //   console.log("📥 Input Parameters:");
  //   console.log("   - Tonnage:", tonnage);
  //   console.log("   - CostData exists:", !!costData);
  //   console.log("   - CostData source:", costData?._source);
  //   console.log("   - ProductData exists:", !!productData);

  //   if (!costData || costData._source !== "stored_procedure" || !productData) {
  //     console.log("⚠️ Early return: Missing costData or productData");
  //     return;
  //   }

  //   try {
  //     const manufacturingCountry = productData.manufacturingCountry || "";
  //     const materialName = productData.materialSelection || null;
  //     const partWeightG = productData.partWeightDimensions?.finishWeight || 0;

  //     console.log("📋 Product Data:");
  //     console.log("   - Manufacturing Country:", manufacturingCountry);
  //     console.log("   - Material Name:", materialName);
  //     console.log("   - Part Weight (g):", partWeightG);

  //     if (!manufacturingCountry || !tonnage || partWeightG <= 0) {
  //       console.warn("⚠️ Cannot update material cost: missing country, tonnage, or part weight");
  //       console.log("   - Country:", manufacturingCountry || "MISSING");
  //       console.log("   - Tonnage:", tonnage || "MISSING");
  //       console.log("   - Part Weight:", partWeightG || "MISSING");
  //       return;
  //     }

  //     // Get current material cost before update
  //     const currentMaterialCost = costData.breakdown?.material || 0;
  //     const currentMaterialPricePerKg = costData.meta?.materialPricePerKg || null;
      
  //     console.log("\n📊 Current Values:");
  //     console.log("   - Current Material Cost:", `$${currentMaterialCost}/unit`);
  //     console.log("   - Current Material Price:", currentMaterialPricePerKg ? `$${currentMaterialPricePerKg}/kg` : "N/A");

  //     const requestPayload = {
  //       tonnage: tonnage,
  //       manufacturingCountry: manufacturingCountry,
  //       materialName: materialName,
  //     };

  //     console.log("\n📤 API Request Payload:");
  //     console.log(JSON.stringify(requestPayload, null, 2));
  //     console.log(`\n🔁 Fetching material price for ${tonnage} tons in ${manufacturingCountry}...`);

  //     const response = await apiFetch("/api/mech/material-price", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(requestPayload),
  //     });

  //     console.log("📥 API Response Status:", response.status, response.statusText);

  //     if (!response.ok) {
  //       const errorData = await response.json().catch(() => ({ error: "Failed to fetch material price" }));
  //       console.error("❌ API Error Response:", errorData);
  //       throw new Error(errorData.error || "Failed to fetch material price");
  //     }

  //     const result = await response.json();
  //     console.log("\n📥 API Response Data:");
  //     console.log(JSON.stringify(result, null, 2));

  //     const newMaterialPricePerKg = parseFloat(result.material_price || 0);
  //     console.log("\n💰 Material Price Parsed:");
  //     console.log("   - Raw value:", result.material_price);
  //     console.log("   - Parsed value:", newMaterialPricePerKg);

  //     if (isNaN(newMaterialPricePerKg) || newMaterialPricePerKg <= 0) {
  //       console.warn("⚠️ Invalid material price received:", result);
  //       return;
  //     }

  //     // Calculate new material cost per unit: (part_weight_g / 1000) * material_price_per_kg
  //     // const partWeightKg = parseFloat(partWeightG) / 1000;
  //     // const newMaterialCost = Number((partWeightKg * newMaterialPricePerKg).toFixed(4));
  //     // Update Material in cost category table with material price per kg directly
  //     const newMaterialCost = Number(Number(newMaterialPricePerKg).toFixed(4));

  //     console.log("\n🧮 Calculation:");
  //     console.log("   - Material Price ($/kg):", newMaterialPricePerKg);
  //     console.log("   - Using material price per kg directly as material cost");
  //     console.log("   - New Material Cost ($/unit):", newMaterialCost);

  //     // Update costData with new material cost
  //     const to4 = (n) => {
  //       if (n === null || n === undefined || n === '') return 0;
  //       if (typeof n === 'string') {
  //         const cleaned = n.replace(/[$,\s]/g, '').trim();
  //         const parsed = parseFloat(cleaned);
  //         return isNaN(parsed) ? 0 : Number(Number(parsed).toFixed(4));
  //       }
  //       return Number(Number(n).toFixed(4));
  //     };

  //     const process = to4(costData.breakdown.process || 0);
  //     const ohp = to4(costData.breakdown.ohp || 0);
  //     const newTotal = Number((newMaterialCost + process + ohp).toFixed(4));

  //     console.log("\n📊 Cost Breakdown Update:");
  //     console.log("   - Material (old):", `$${currentMaterialCost}/unit`);
  //     console.log("   - Material (new):", `$${newMaterialCost}/unit`);
  //     console.log("   - Process:", `$${process}/unit`);
  //     console.log("   - OHP:", `$${ohp}/unit`);
  //     console.log("   - Total (old):", `$${costData.breakdown.total_should_cost}/unit`);
  //     console.log("   - Total (new):", `$${newTotal}/unit`);
  //     console.log("   - Total Formula:", `${newMaterialCost} + ${process} + ${ohp} = ${newTotal}`);

  //     const updatedCostData = {
  //       ...costData,
  //       breakdown: {
  //         ...costData.breakdown,
  //         material: newMaterialCost,
  //         total_should_cost: newTotal,
  //       },
  //       summary: costData.summary.replace(
  //         /\$[\d.]+ per unit/,
  //         `$${newTotal} per unit`
  //       ),
  //       assumptions: costData.assumptions.map((assumption) => {
  //         // Update material price in assumptions if it exists
  //         if (assumption.includes("Material price:")) {
  //           return `Material price: $${newMaterialPricePerKg.toFixed(4)}/kg`;
  //         }
  //         return assumption;
  //       }),
  //       meta: {
  //         ...costData.meta,
  //         materialPricePerKg: newMaterialPricePerKg,
  //       },
  //     };

  //     setCostData(updatedCostData);
  //     console.log("\n✅ Material cost updated successfully!");
  //     console.log("   - New Material Price:", `$${newMaterialPricePerKg}/kg`);
  //     console.log("   - New Material Cost:", `$${newMaterialCost}/unit`);
  //     console.log("   - New Total Cost:", `$${newTotal}/unit`);
  //     console.log("=".repeat(80) + "\n");
  //   } catch (error) {
  //     console.error("\n❌ Failed to update material cost:");
  //     console.error("   - Error type:", error.constructor.name);
  //     console.error("   - Error message:", error.message);
  //     console.error("   - Stack:", error.stack);
  //     console.log("=".repeat(80) + "\n");
  //     // Don't show alert to user, just log the error
  //   }
  // };

  const handlePlayBookChange = (field, value) => {
    // If updating costParams, merge it properly
    if (field === "costParams") {
      const previousOhpRate = playBookData?.costParams?.ohpRate;
      const newOhpRate = value.ohpRate;
      const previousTonnage = playBookData?.costParams?.machineTonnage;
      const newTonnage = value.machineTonnage;
      
      setPlayBookData({
        ...playBookData,
        costParams: value,
      });
      setKeyInsightsData(null);

      // If OHP Rate changed and we have existing costData, recalculate OHP cost
      // if (costData && costData._source === "stored_procedure" && 
      //     newOhpRate !== undefined && newOhpRate !== null && 
      //     newOhpRate !== previousOhpRate) {
      //   const updatedCostData = recalculateOHPCost(newOhpRate, costData, value);
      //   setCostData(updatedCostData);
      // }

      // Note: Material price update is now handled when Quick Cost button is clicked
      // No automatic update here - wait for user to click Quick Cost button
    } else {
    setPlayBookData({ ...playBookData, [field]: value });
    }
  };

  // Helper function to analyze cost with explicit data
  // const analyzeCostWithData = async (recipe, playbook, product) => {
  //   try {
  //     console.log("🚀 Analyzing cost, risk, and substitutes with:", { recipe, playbook, product });

  //     const payload = {
  //       recipe: recipe,
  //       playBookData: playbook,
  //       productData: product,
  //     };

  //     // Call all three APIs in parallel
  //     const [costResponse, riskResponse, substituteResponse] = await Promise.all([
  //       apiFetch("/api/fnb/analyze-cost", {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify(payload),
  //       }),
  //       apiFetch("/api/fnb/analyze-risk", {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify(payload),
  //       }),
  //       apiFetch("/api/fnb/find-substitutes", {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify(payload),
  //       }),
  //     ]);

  //     // Check responses
  //     if (!costResponse.ok) {
  //       const errorData = await costResponse
  //         .json()
  //         .catch(() => ({ error: "Cost analysis failed" }));
  //       throw new Error(errorData.error || "Failed to analyze cost");
  //     }

  //     // Parse all responses (non-blocking for risk and substitute)
  //     const costResult = await costResponse.json();
  //     console.log("✅ Cost analysis complete:", costResult);
  //     setCostData(costResult);

  //     // Handle risk analysis
  //     if (riskResponse.ok) {
  //       const riskResult = await riskResponse.json();
  //       console.log("✅ Risk analysis complete:", riskResult);
  //       setRiskData(riskResult);
  //     } else {
  //       console.warn("⚠️ Risk analysis failed, continuing without risk data");
  //       setRiskData(null);
  //     }

  //     // Handle substitute analysis
  //     if (substituteResponse.ok) {
  //       const substituteResult = await substituteResponse.json();
  //       console.log("✅ Substitute analysis complete:", substituteResult);
  //       setSubstituteData(substituteResult);
  //     } else {
  //       console.warn("⚠️ Substitute analysis failed, continuing without substitute data");
  //       setSubstituteData(null);
  //     }

  //     // Generate product image with the playbook data
  //     await generateProductImageWithData(product, playbook);

  //     // Navigate to results section and mark as started
  //     setActiveSection("results");
  //     setSectionStarted((prev) => ({ ...prev, results: true }));

  //     return costResult;
  //   } catch (error) {
  //     console.error("Analysis failed:", error);
  //     alert(`Failed to complete analysis: ${error.message}`);
  //     throw error;
  //   }
  // };

  // const handleAnalyzeCost = async () => {
  //   if (!recipeData || !playBookData || !productData) return;

  //   setLoadingMessage("Running cost analysis...");
  //   setIsProcessing(true);
  //   try {
  //     await analyzeCostWithData(recipeData, playBookData, productData);
  //   } finally {
  //     setLoadingMessage("");
  //     setIsProcessing(false);
  //   }
  // };

  const generateProductImageWithData = async (product, playbook) => {
    if (!product) return;

    setGeneratingImage(true);

    try {
      const imagePayload = {
        productName: product.productName || "",
        materialSelection:
          product.materialSelection?.material ||
          product.materialSelection?.name ||
          product.materialSelection ||
          "",
        processSelection: product.processSelection || "",
        partWeightDimensions: product.partWeightDimensions || {},
        companyName: "Kearney",
        brandName: "PERLab",
        commodity: "Casting",
        primaryPackaging: playbook?.primary_packaging || "",
        skuWt: product.skuWeight || "",
        skuWtUnit: product.skuWeightUnit || "g",
        marketPositioning: product.marketPositioning || "",
        style: "professional",
      };

      console.log("🖼️ Generating product image:", imagePayload);

      // const response = await apiFetch("/api/ai/generate-product-image", {
      const response = await apiFetch("/api/mech/casting/generate-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imagePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate image");
      }

      const data = await response.json();
      setProductImageUrl(data.image_url);
      console.log("✅ Product image generated:", data.image_url);
    } catch (error) {
      console.error("❌ Error generating product image:", error);
      // Don't alert - image is optional
    } finally {
      setGeneratingImage(false);
    }
  };

  const generateProductImage = () => {
    return generateProductImageWithData(productData, playBookData);
  };

  // Fetch risk and substitute analysis for Analysis Report tabs.
  const fetchRiskAndSubstituteData = async (product, playbook, costBreakdownOverride = null) => {
    if (!product) {
      setRiskData(null);
      setSubstituteData(null);
      return;
    }

    const payload = {
      towerName: "Mechanical",
      costModel: costBreakdownOverride || costData?.breakdown || {},
      form: {
        productData: product || {},
        playBookData: playbook || {},
        recipe: recipeData || [],
      },
    };

    const fallbackRecipe = Array.isArray(recipeData) && recipeData.length > 0
      ? recipeData
      : [{ ingredient: product?.materialSelection || "Casting Alloy", percentage: 100, functional_group: "Base" }];

    const fnbPayload = {
      recipe: fallbackRecipe,
      playBookData: playbook || {},
      productData: product || {},
    };

    const buildFallbackRisk = () => ({
      summary: `Risk snapshot for ${product?.productName || "casting part"} based on available costing inputs.`,
      top_concerns: [
        "Alloy price and supply volatility can shift should-cost.",
        "Cycle and yield variation can increase process cost.",
        "Foundry capacity and quality drift can impact lead-time.",
      ],
      risk_items: [
        { title: "Alloy Cost Volatility", severity: 6, probability: 6, detail: "Track alloy index and supplier agreements." },
        { title: "Process Yield Variability", severity: 5, probability: 5, detail: "Monitor scrap/rework and stabilize process window." },
      ],
      assumptions: "Generated from casting product and playbook inputs (fallback mode).",
    });

    const buildFallbackSubstitute = () => {
      const params = playbook?.costParams || {};
      const material = product?.materialSelection || "Current alloy";
      const runnerType = params.runnerType || "Current runner setup";
      const cavities = params.cavities || "Current cavities";
      const cycleTime = params.cycleTime || "Current cycle time";
      const tonnage = params.machineTonnage || "Current tonnage";

      return {
        summary: `Casting process optimization recommendations generated for ${product?.productName || "the current part"} using provided inputs.`,
        substitutes: [
          {
            current_item: `Alloy/material: ${material}`,
            proposed_item: "Validate equivalent lower-cost grade/alloy",
            estimated_cost_delta_per_unit: "Potential material cost reduction",
            impact_on_quality: "Requires validation",
            feasibility_score: 6,
            timeline_weeks: 4,
            risk_note: "Validate strength, porosity, and machinability.",
            validation_steps: ["Material equivalence review", "Pilot casting run", "Mechanical testing"],
          },
          {
            current_item: `Runner setup: ${runnerType}, cavities: ${cavities}`,
            proposed_item: "Optimize gating/runner design to improve yield",
            estimated_cost_delta_per_unit: "Potential process/yield savings",
            impact_on_quality: "Low to medium",
            feasibility_score: 7,
            timeline_weeks: 3,
            risk_note: "Confirm fill quality and defect rate after changes.",
            validation_steps: ["Mold flow review", "Trial run", "Scrap analysis"],
          },
          {
            current_item: `Cycle time: ${cycleTime}, tonnage: ${tonnage}`,
            proposed_item: "Tune cycle and machine settings for throughput gains",
            estimated_cost_delta_per_unit: "Potential conversion cost reduction",
            impact_on_quality: "Process-dependent",
            feasibility_score: 7,
            timeline_weeks: 2,
            risk_note: "Monitor dimensional and metallurgical consistency.",
            validation_steps: ["DOE on settings", "Capability study", "Control plan update"],
          },
        ],
        prioritization: [
          "Process tuning for immediate throughput improvement",
          "Runner/gating optimization to reduce scrap",
          "Material/alloy optimization after process stabilization",
        ],
        assumptions: "Generated from casting product and playbook inputs (fallback mode).",
      };
    };

    try {
      let [riskResponse, substituteResponse] = await Promise.all([
        apiFetch("/api/ai/costing-v4/casting/risk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/ai/costing-v4/substitute", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      if (!riskResponse.ok || !substituteResponse.ok) {
        [riskResponse, substituteResponse] = await Promise.all([
          apiFetch("/api/fnb/analyze-risk", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
          apiFetch("/api/fnb/find-substitutes", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
        ]);
      }

      if (riskResponse.ok) {
        setRiskData(normalizeCastingRiskData(await riskResponse.json()));
      } else {
        setRiskData(normalizeCastingRiskData(buildFallbackRisk()));
      }

      if (substituteResponse.ok) {
        setSubstituteData(normalizeCastingSubstituteData(await substituteResponse.json()));
      } else {
        setSubstituteData(normalizeCastingSubstituteData(buildFallbackSubstitute()));
      }
    } catch (error) {
      console.error("Risk/Substitute analysis failed:", error);
      setRiskData(normalizeCastingRiskData(buildFallbackRisk()));
      setSubstituteData(normalizeCastingSubstituteData(buildFallbackSubstitute()));
    }
  };

  // const fetchKeyInsights = async (product, playbook) => {
  //   if (!product) {
  //     return;
  //   }
  //   try {
  //     const response = await apiFetch("/api/mech/key-insights", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({
  //         productData: product,
  //         playBookData: playbook || {},
  //       }),
  //     });
  //     if (!response.ok) {
  //       const errorText = await response.text();
  //       throw new Error(errorText || "Failed to generate key insights");
  //     }
  //     const insights = await response.json();
  //     setKeyInsightsData(insights);
  //   } catch (error) {
  //     console.error("Failed to fetch key insights:", error);
  //   }
  // };

  // // Call stored procedure QC_SP_Plastics
  // const callStoredProcedure = async (productData, playBookData) => {
  //   try {
  //     // Get logged-in username
  //     let username = "User";
  //     try {
  //       const userRes = await apiFetch("/api/user/info");
  //       if (userRes.ok) {
  //         const userData = await userRes.json();
  //         username = userData.username || userData.fullName || "User";
  //       }
  //     } catch (err) {
  //       console.warn("Failed to fetch user info:", err);
  //     }

  //     // Extract dimension data from productData
  //     const dimensionData = productData.partWeightDimensions || {};
      
  //     // Extract details data from productData
  //     const detailsData = productData.partDetails || {};
      
  //     // Get Batch size from productData
  //     const batchSizeValue = productData.batchSize || "";
  //     let batchSize = 0;
  //     if (batchSizeValue) {
  //       if (typeof batchSizeValue === 'number') {
  //         batchSize = batchSizeValue;
  //       } else if (typeof batchSizeValue === 'string') {
  //         const parsed = parseFloat(batchSizeValue);
  //         if (!isNaN(parsed)) {
  //           batchSize = parsed;
  //         }
  //       }
  //     }

  //     // Get costParams from playBookData
  //     const costParams = playBookData?.costParams || {};

  //     // Prepare stored procedure parameters
  //     const spParams = {
  //       ID: 0, // Default 0
  //       PartName: productData.productName || "",
  //       MfrCountry: productData.manufacturingCountry || "",
  //       BatchSize: batchSize,
  //       MaterialName: productData.materialSelection || "",
  //       PartLength: dimensionData.partLength || "",
  //       PartWidth: dimensionData.partWidth || "",
  //       PartHeight: dimensionData.partHeight || "",
  //       PartWeight: dimensionData.finishWeight || "",
  //       AvgWallThick: detailsData.avgWallThickness || "",
  //       Finish: "2025-Q2A", // Hardcoded
  //       RunnerType: detailsData.runnerType || costParams.runnerType || "N/A",
  //       Regrind: detailsData.regrindPct || "",
  //       Cavities: detailsData.noOfCavities || costParams.cavities || 0,
  //       Tonnage: detailsData.machineTonnage || costParams.machineTonnage || 0,
  //       CreatedBy: username,
  //       matPrice: costParams.matPrice || 0,
  //       GedbQtr: "2025-Q2A", // Hardcoded
  //     };

  //     console.log("📞 Calling stored procedure QC_SP_Plastics with params:", spParams);

  //     // Call the stored procedure API endpoint
  //     const response = await apiFetch("/api/costing/plastics", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(spParams),
  //     });

  //     if (!response.ok) {
  //       const errorData = await response.json().catch(() => ({}));
  //       throw new Error(errorData.error || `Stored procedure call failed (${response.status})`);
  //     }

  //     const result = await response.json();
  //     console.log("✅ Stored procedure result:", result);
  //     return result;
  //   } catch (err) {
  //     console.error("❌ Stored procedure call failed:", err);
  //     throw err;
  //   }
  // };

  const getTimestampId = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `${year}${month}${day}${hours}${minutes}${seconds}`;
  };


  // Function to render product information and assumptions
  const renderAssumptions = React.useMemo(() => {
    if (!productData) return null;

    const perlTypography = theme.custom?.perlTypography || {};
    const assumptions = costData?.assumptions || {};

    // Build product information from selected values
    const buildProductInfo = () => {
      const productInfo = [];

      // Product Name
      if (productData.productName) {
        productInfo.push(`Product Name: ${productData.productName}`);
      }

      // Material Selection (with grade if available)
      if (productData.materialSelection) {
        const materialDisplay = productData.materialSelection + 
          (productData.materialGrade ? `-${productData.materialGrade}` : "");
        productInfo.push(`Material: ${materialDisplay}`);
      }

      // Batch Size
      if (productData.batchSize) {
        productInfo.push(`Batch Size: ${productData.batchSize}`);
      }

      // Part Dimensions
      if (productData.partWeightDimensions?.finishWeight &&
          productData.partWeightDimensions?.partLength &&
          productData.partWeightDimensions?.partWidth &&
          productData.partWeightDimensions?.partHeight) {
        const dims = productData.partWeightDimensions;
        productInfo.push(
          `Part Dimensions: L: ${dims.partLength}mm, W: ${dims.partWidth}mm, H: ${dims.partHeight}mm, Weight: ${dims.finishWeight}g`
        );
      }

      // Part Details
      const partDetails = [];
      if (productData.partDetails?.avgWallThickness) {
        partDetails.push(`Wall: ${productData.partDetails.avgWallThickness}mm`);
      }
      if (productData.partDetails?.noOfCavities) {
        partDetails.push(`Cavities: ${productData.partDetails.noOfCavities}`);
      }
      if (partDetails.length > 0) {
        productInfo.push(`Part Details: ${partDetails.join(", ")}`);
      }

      // Process Involved
      const processes = [];
      if (productData.processInvolved?.sandBlasting) {
        processes.push("Sand Blasting");
      }
      if (productData.processInvolved?.deburing) {
        processes.push("Deburring");
      }
      if (productData.processInvolved?.heatTreatment) {
        processes.push("Heat Treatment");
      }
      if (processes.length > 0) {
        productInfo.push(`Process Involved: ${processes.join(", ")}`);
      }

      // Manufacturing Country
      if (productData.manufacturingCountry) {
        productInfo.push(`Manufacturing Country: ${productData.manufacturingCountry}`);
      }

      return productInfo;
    };

    const productInfoItems = buildProductInfo();

    return (
      <Box>
        <Paper
          sx={{
            p: 3,
            bgcolor: perlColors.surface,
            border: `1px solid ${perlColors.border}`,
            borderRadius: 2,
            mt: 3,
          }}
        >
          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 2,
                  fontSize: "14px",
                  fontWeight: 600,
                  lineHeight: "18px",
                  fontFamily: perlTypography.family,
                  letterSpacing: "0.3px",
                  color: perlColors.textPrimary,
                }}
              >
                Product Information
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {productInfoItems.map((item, idx) => {
                  const [label, value] = item.split(": ");
                  return (
                    <Box
                      key={idx}
                      component="li"
                      sx={{
                        mb: 1.5,
                        fontSize: "13px",
                        lineHeight: "20px",
                        color: perlColors.textSecondary,
                        fontFamily: perlTypography.family,
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 500, color: perlColors.textPrimary }}>
                        {label}:
                      </Box>{" "}
                      {value}
                    </Box>
                  );
                })}
              </Box>
            </Box>
            
            {/* Assumptions Section */}
            {(() => {
              // Get input values from assumptions
              const inputAssumptions = assumptions?.Inputs || [];
              
              // Build assumptions list (combine all sections)
              const allAssumptions = [];
              if (inputAssumptions.length > 0) {
                allAssumptions.push(...inputAssumptions);
              }
              
              // Add other assumption sections if they exist
              Object.keys(assumptions).forEach((section) => {
                if (section !== 'Inputs' && Array.isArray(assumptions[section])) {
                  allAssumptions.push(...assumptions[section]);
                }
              });
              
              if (allAssumptions.length === 0) return null;
              
              return (
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      fontFamily: perlTypography.family,
                      letterSpacing: "0.3px",
                      color: perlColors.textPrimary,
                    }}
                  >
                    Assumptions
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {allAssumptions.map((assumption, idx) => {
                      // Handle both string and object formats
                      const assumptionText = typeof assumption === 'string' 
                        ? assumption 
                        : (assumption?.value || assumption?.name || assumption?.reason || JSON.stringify(assumption));
                      
                      // Try to split by colon for label/value format
                      const parts = assumptionText.split(": ");
                      const label = parts.length > 1 ? parts[0] : null;
                      const value = parts.length > 1 ? parts.slice(1).join(": ") : assumptionText;
                      
                      return (
                        <Box
                          key={idx}
                          component="li"
                          sx={{
                            mb: 1.5,
                            fontSize: "13px",
                            lineHeight: "20px",
                            color: perlColors.textSecondary,
                            fontFamily: perlTypography.family,
                          }}
                        >
                          {label ? (
                            <>
                              <Box component="span" sx={{ fontWeight: 500, color: perlColors.textPrimary }}>
                                {label}:
                              </Box>{" "}
                              {value}
                            </>
                          ) : (
                            assumptionText
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })()}
          </Box>
        </Paper>
      </Box>
    );
  }, [productData, costData, perlColors, theme]);

  const handleProgressiveQuickCost = async () => {
    if (!productData) {
      alert("Please fill in product information first");
      return;
    }

    console.log("Starting Progressive Quick Cost Analysis with SSE...");

    // Initialize progress steps immediately
    const totalSteps = 6;
    const initialSteps = Array.from({ length: totalSteps }, (_, i) => ({
      id: `step-${i + 1}`,
      label: i === 0 ? "Processing..." : `Step ${i + 1}`,
      status: "pending",
      message: "",
    }));
    setProgressSteps(initialSteps);
    setCurrentProgressStep(1);
    setOverallProgress(0);
    setIsProcessing(true);
    setShowProgressTracker(true);
    setRiskData(null);
    setSubstituteData(null);

    // Track maximum progress to prevent going backwards
    let maxProgress = 0;

    try {
      const timestamp = getTimestampId();

      // Prepare request payload
      // Extract dimension data from productData
      const dimensionData = productData.partWeightDimensions || {};
      
      // Extract details data from productData
      const detailsData = productData.partDetails || {};

      const processInvolved = productData.processInvolved

      const Process = [
        processInvolved?.sandBlasting &&
          `Sand Blasting`,
        processInvolved?.deburing &&
          `Deburring`,
        processInvolved?.heatTreatment &&
          `Heat Treatment`,
        
      ]
        .filter(Boolean)
        .join(", ")
      
      // Get Batch size from productData
      const batchSizeValue = productData.batchSize || "";
      let batchSize = 0;
      if (batchSizeValue) {
        if (typeof batchSizeValue === 'number') {
          batchSize = batchSizeValue;
        } else if (typeof batchSizeValue === 'string') {
          const parsed = parseFloat(batchSizeValue);
          if (!isNaN(parsed)) {
            batchSize = parsed;
          }
        }
      }

      const params = {
          commodity: "CASTINGS",
          operation: "unified_quick_cost",
          userEmail: user?.email || "",
          PartName: productData.productName || "",
          MfrCountry: productData.manufacturingCountry || "",
          BatchSize: batchSize,
          MaterialName: productData.materialSelection + (productData.materialGrade?"-"+productData.materialGrade:""),
          PartLength: dimensionData.partLength || "",
          PartWidth: dimensionData.partWidth || "",
          PartHeight: dimensionData.partHeight || "",
          PartWeight: dimensionData.finishWeight || "",
          AvgWallThick: detailsData.avgWallThickness || "",
          ProcessInvolved: Process|| "N/A",
          // RunnerType: detailsData.runnerType || costParams.runnerType || "N/A",
          // Cavities: detailsData.noOfCavities || costParams.cavities || 0,
          // Cavities: detailsData.noOfCavities || 0,
          // Tonnage: detailsData.machineTonnage || costParams.machineTonnage || 0,
          // CreatedBy: username,
          // matPrice: costParams.matPrice || 0,
          // GedbQtr: "2025-Q2A", // Hardcoded
        };

      const costParams = playBookData?.costParams || {};
      const oldCostParams = oldPlayBookData?.costParams || {};
      params.Cavities = costParams.cavities || detailsData.noOfCavities || "N/A";

      if(!playBookData ) {
        params.CycleTime = "N/A";
        params.Tonnage = "N/A";
        params.OhpRate = "N/A";
        params.RunnerType = "N/A";
      }
      else{
        if (oldCostParams.cycleTime != costParams.cycleTime) {
          params.CycleTime = costParams.cycleTime
        }else{params.CycleTime = "N/A";}

        if (oldCostParams.machineTonnage != costParams.machineTonnage) {
          params.Tonnage = costParams.machineTonnage
        }else{params.Tonnage = "N/A";}

        if (oldCostParams.ohpRate != costParams.ohpRate) {
          params.OhpRate = costParams.ohpRate
        }else{params.OhpRate = "N/A";}

        if (oldCostParams.runnerType != costParams.runnerType) {
          params.RunnerType = costParams.runnerType
        }else{params.RunnerType = "N/A";}

      };

      console.log("Calling streaming endpoint:", params);

      // Call SSE streaming endpoint
      const response = await apiFetch("/api/mech/casting/execute-complete-analysis-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete events (separated by \n\n)
        const events = buffer.split("\n\n");
        buffer = events.pop() || ""; // Keep incomplete event in buffer

        for (const event of events) {
          if (!event.trim()) continue;

          // Extract JSON from "data: {...}" format
          const dataMatch = event.match(/^data: (.+)$/m);
          if (!dataMatch) continue;

          try {
            const eventData = JSON.parse(dataMatch[1]);
            console.log("≡ƒô¿ SSE Event:", eventData);

            if (eventData.type === "start") {
              // Update total steps if different from initial estimate
              const totalSteps = eventData.total_steps || 10;
              if (totalSteps !== progressSteps.length) {
                const updatedSteps = Array.from({ length: totalSteps }, (_, i) => ({
                  id: `step-${i + 1}`,
                  label: `Step ${i + 1}`,
                  status: "pending",
                  message: "",
                }));
                setProgressSteps(updatedSteps);
              }
            } else if (eventData.type === "progress") {
              // Update specific step status
              const stepIndex = eventData.step - 1;
              console.log(
                `≡ƒôè Progress Event - Step ${eventData.step}: ${eventData.label} (${eventData.status})`
              );

              setProgressSteps((prev) => {
                const updated = [...prev];
                if (updated[stepIndex]) {
                  // Map backend status to frontend status
                  let mappedStatus = eventData.status;
                  if (eventData.status === "running") {
                    mappedStatus = "in-progress";
                  } else if (eventData.status === "success") {
                    mappedStatus = "completed";
                  }

                  updated[stepIndex] = {
                    ...updated[stepIndex],
                    label: eventData.label || updated[stepIndex].label,
                    status: mappedStatus,
                    message:
                      eventData.message ||
                      (eventData.status === "running" ? "Processing..." : "Complete"),
                    error: eventData.error,
                  };

                  console.log(
                    `   Γ£à Updated step ${eventData.step}: label="${updated[stepIndex].label}", status="${updated[stepIndex].status}"`
                  );
                }
                return updated;
              });

              // Update current step based on the most recent event
              // For parallel execution, show the step that just changed status
              if (eventData.status === "running" || eventData.status === "success") {
                setCurrentProgressStep(eventData.step);
              }

              const newProgress = (eventData.step / eventData.total) * 100;
              if (newProgress > maxProgress) {
                maxProgress = newProgress;
                setOverallProgress(newProgress);
              }
            } else if (eventData.type === "complete") {
              // Analysis complete - populate all state from final response
              console.log("Γ£à Analysis complete:", eventData);
              console.log("≡ƒöì Complete event keys:", Object.keys(eventData));
              console.log("≡ƒöì Has playBookData?", !!eventData.playBookData);
              console.log("≡ƒöì Has riskData?", !!eventData.riskData);
              console.log("≡ƒöì Has substituteData?", !!eventData.substituteData);
              console.log("≡ƒöì Has productImage?", !!eventData.productImage);
              if (eventData.productImage) {
                console.log("≡ƒû╝∩╕Å Product image data:", eventData.productImage);
              }

              // Set progress to 100%
              maxProgress = 100;
              setOverallProgress(100);

              // Mark all steps as completed
              setProgressSteps((prev) =>
                prev.map((step) => ({
                  ...step,
                  status: "completed",
                  message: "Complete",
                }))
              );

              // Populate state from complete event data
              // Initialize playbook data - use provided playBookData or extract from assumptions/cost result
              let playbookToSet = null;
              
              if (eventData.playBookData) {
                // Backend wraps data in extra layer, unwrap it
                const unwrappedPlayBookData =
                  eventData.playBookData.playBookData || eventData.playBookData;
                console.log("≡ƒôï Setting playbook data from eventData:", unwrappedPlayBookData);
                console.log("≡ƒöì Playbook data keys:", Object.keys(unwrappedPlayBookData));
                playbookToSet = unwrappedPlayBookData;
              } else if (eventData.Playbook_Data) {
                // Check for Playbook_Data field (similar to forging)
                const playbookData = eventData.Playbook_Data;
                console.log("≡ƒôï Setting playbook data from Playbook_Data:", playbookData);
                playbookToSet = playbookData;
              } else {
                // Initialize playbook data from assumptions or cost result if not provided
                // Similar to how SheetmetalPage initializes from stored procedure result
                const existingCostParams = playBookData?.costParams || {};
                const isFirstRun = !playBookData || !playBookData.costParams;
                
                // Helper function to parse numeric values
                const parseNumericValue = (value, defaultValue = 0) => {
                  if (value === null || value === undefined || value === "") return defaultValue;
                  if (typeof value === "number") return isNaN(value) ? defaultValue : value;
                  const parsed = parseFloat(value);
                  return isNaN(parsed) ? defaultValue : parsed;
                };
                
                // Extract values from assumptions if available
                const assumptions = eventData.assumptions || {};
                const inputAssumptions = assumptions.Inputs || [];
                
                // Try to extract values from assumptions
                let cycleTime = 0;
                let machineTonnage = 0;
                let ohpRate = 0;
                let cavities = 0;
                let runnerType = "N/A";
                
                // Parse assumptions to extract values
                inputAssumptions.forEach((assumption) => {
                  const assumptionText = typeof assumption === 'string' 
                    ? assumption 
                    : (assumption?.value || assumption?.name || "");
                  
                  if (assumptionText.includes("Cycle Time") || assumptionText.includes("Cycle time")) {
                    const match = assumptionText.match(/(\d+\.?\d*)/);
                    if (match) cycleTime = parseNumericValue(match[1]);
                  }
                  if (assumptionText.includes("Tonnage") || assumptionText.includes("tonnage")) {
                    const match = assumptionText.match(/(\d+\.?\d*)/);
                    if (match) machineTonnage = parseNumericValue(match[1]);
                  }
                  if (assumptionText.includes("OHP") || assumptionText.includes("Overhead")) {
                    const match = assumptionText.match(/(\d+\.?\d*)/);
                    if (match) ohpRate = parseNumericValue(match[1]);
                  }
                  if (assumptionText.includes("Cavity") || assumptionText.includes("Cavities")) {
                    const match = assumptionText.match(/(\d+)/);
                    if (match) cavities = parseInt(match[1]) || 0;
                  }
                  if (assumptionText.includes("Runner Type") || assumptionText.includes("Runner type")) {
                    const match = assumptionText.match(/Runner [Tt]ype[:\s]+(.+)/i);
                    if (match) runnerType = match[1].trim() || "N/A";
                  }
                });
                
                // Also check process_parameters in assumptions if available
                if (assumptions.process_parameters) {
                  const processParams = assumptions.process_parameters;
                  if (processParams.cycle_time_sec) cycleTime = parseNumericValue(processParams.cycle_time_sec);
                  if (processParams.tonnage) machineTonnage = parseNumericValue(processParams.tonnage);
                  if (processParams.overhead_percentage) ohpRate = parseNumericValue(assumptions.process_parameters.overhead_percentage);
                }
                
                // Create playbook structure similar to SheetmetalPage
                playbookToSet = {
                  costParams: {
                    // On first run, use extracted values; on subsequent runs, preserve existing values if they exist
                    cycleTime: (isFirstRun || existingCostParams.cycleTime === 0) 
                      ? cycleTime 
                      : existingCostParams.cycleTime,
                    machineTonnage: (isFirstRun || existingCostParams.machineTonnage === 0) 
                      ? machineTonnage 
                      : existingCostParams.machineTonnage,
                    ohpRate: (isFirstRun || existingCostParams.ohpRate === 0) 
                      ? ohpRate 
                      : existingCostParams.ohpRate,
                    cavities: (isFirstRun || existingCostParams.cavities === 0) 
                      ? cavities 
                      : existingCostParams.cavities,
                    runnerType: existingCostParams.runnerType || runnerType,
                  },
                };
                
                console.log("🔧 [CastingPage] Initialized playbook data from assumptions:", {
                  isFirstRun,
                  playbookToSet,
                  existingCostParams,
                });
              }
              
              if (playbookToSet) {
                setPlayBookData(playbookToSet);
                setOldPlayBookData(playbookToSet);
              }

              
              if (eventData.ShouldCostResult) {
                console.log("≡ƒÆ░ Setting cost data");
                const costDataResult = {
                  breakdown: {
                    material: eventData.ShouldCostResult.MaterialCost || 0,
                    process: eventData.ShouldCostResult.ProcessCost || 0,
                    // packaging: eventData.ShouldCostResult.PackagingCost || 0,
                    ohp: eventData.ShouldCostResult.OHPCost || 0,
                  },
                  total: eventData.ShouldCostResult.TotalCost || 0, // Pass the correct total from DB
                  assumptions: eventData.assumptions || {}, // Include assumptions in costData
                };
                setCostData(costDataResult);
                if (eventData.riskData) {
                  setRiskData(normalizeCastingRiskData(eventData.riskData));
                }
                if (eventData.substituteData) {
                  setSubstituteData(normalizeCastingSubstituteData(eventData.substituteData));
                }
                if (!eventData.riskData || !eventData.substituteData) {
                  await fetchRiskAndSubstituteData(
                    productData,
                    playbookToSet || playBookData,
                    costDataResult.breakdown
                  );
                }
              } else if (eventData.assumptions) {
                // If there's no ShouldCostResult but there are assumptions, update costData with assumptions
                console.log("📝 Setting assumptions data:", eventData.assumptions);
                console.log("   Assumptions sections:", Object.keys(eventData.assumptions));
                setCostData((prev) => ({
                  ...prev,
                  assumptions: eventData.assumptions,
                }));
              }

              // Extract keyInsights from response (can be keyInsights or KeyInsightsJson)
              if (eventData.keyInsights) {
                // Direct keyInsights object with cost_savings and strategic arrays
                console.log("🔑 Setting key insights data:", eventData.keyInsights);
                console.log("   Key insights keys:", Object.keys(eventData.keyInsights));
                console.log("   Has cost_savings?", !!eventData.keyInsights.cost_savings);
                console.log("   Has strategic?", !!eventData.keyInsights.strategic);
                setKeyInsightsData(eventData.keyInsights);
              } else if (eventData.KeyInsightsJson) {
                // Backend wraps data in extra layer, unwrap it
                const unwrappedKeyInsights =
                  eventData.KeyInsightsJson.key_insights || eventData.KeyInsightsJson;
                console.log("🔑 Setting key insights data (from KeyInsightsJson):", unwrappedKeyInsights);
                console.log("   Key insights keys:", Object.keys(unwrappedKeyInsights));
                console.log("   Has cost_savings?", !!unwrappedKeyInsights.cost_savings);
                console.log("   Has strategic?", !!unwrappedKeyInsights.strategic);
                setKeyInsightsData(unwrappedKeyInsights);
              }

              // Generate product image with the playbook data
              if (playbookToSet) {
                await generateProductImageWithData(productData, playbookToSet);
              }

              // Close dialog immediately and navigate to playbook first, then results
              setShowProgressTracker(false);
              setIsProcessing(false);
              // Navigate to playbook section to view playbook data
              setActiveSection("playbook");
              setSectionStarted((prev) => ({
                ...prev,
                formulation: true,
                playbook: true,
                results: true,
              }));
              
              // After a short delay, navigate to results section
              setTimeout(() => {
                setActiveSection("results");
              }, 1000);

              break;
            } else if (eventData.type === "error") {
              throw new Error(eventData.error || eventData.message || "Analysis failed");
            }
          } catch (parseError) {
            console.error("Γ¥î Error parsing SSE event:", parseError);
          }
        }
      }

      console.log("Γ£à Progressive Quick Cost completed successfully!");
    } catch (error) {
      console.error("Γ¥î Progressive Quick Cost failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      setIsProcessing(false);
      setShowProgressTracker(false);
    }


  };

  const handleQuickCost = async () => {
    
    const Ai_Maintenance = "Schema Management V2";

    if (Ai_Maintenance === "Schema Management V2") {
      handleProgressiveQuickCost();
      return;
    }

    if (!productData) return;

    setLoadingMessage("Generating Quick Cost...");
    setIsProcessing(true);
    setKeyInsightsData(null);
    try {
      // Call F&B orchestration API - AI + Database calculation in one call
      // Extract dimension data from productData
      const dimensionData = productData.partWeightDimensions || {};
      
      // Extract details data from productData
      const detailsData = productData.partDetails || {};

      const processInvolved = productData.processInvolved

      const Process = [
        processInvolved?.sandBlasting &&
          `Sand Blasting`,
        processInvolved?.deburing &&
          `Deburring`,
        processInvolved?.heatTreatment &&
          `Heat Treatment`,
        
      ]
        .filter(Boolean)
        .join(", ")
      
      // Get Batch size from productData
      const batchSizeValue = productData.batchSize || "";
      let batchSize = 0;
      if (batchSizeValue) {
        if (typeof batchSizeValue === 'number') {
          batchSize = batchSizeValue;
        } else if (typeof batchSizeValue === 'string') {
          const parsed = parseFloat(batchSizeValue);
          if (!isNaN(parsed)) {
            batchSize = parsed;
          }
        }
      }

      const params = {
          commodity: "CASTINGS",
          operation: "unified_quick_cost",
          userEmail: user?.email || "",
          PartName: productData.productName || "",
          MfrCountry: productData.manufacturingCountry || "",
          BatchSize: batchSize,
          MaterialName: productData.materialSelection + (productData.materialGrade?"-"+productData.materialGrade:""),
          PartLength: dimensionData.partLength || "",
          PartWidth: dimensionData.partWidth || "",
          PartHeight: dimensionData.partHeight || "",
          PartWeight: dimensionData.finishWeight || "",
          AvgWallThick: detailsData.avgWallThickness || "",
          ProcessInvolved: Process|| "N/A",
          // RunnerType: detailsData.runnerType || costParams.runnerType || "N/A",
          // Cavities: detailsData.noOfCavities || costParams.cavities || 0,
          // Cavities: detailsData.noOfCavities || 0,
          // Tonnage: detailsData.machineTonnage || costParams.machineTonnage || 0,
          // CreatedBy: username,
          // matPrice: costParams.matPrice || 0,
          // GedbQtr: "2025-Q2A", // Hardcoded
        };

      const costParams = playBookData?.costParams || {};
      const oldCostParams = oldPlayBookData?.costParams || {};
      params.Cavities = costParams.cavities || detailsData.noOfCavities || "N/A";

      if(!playBookData ) {
        params.CycleTime = "N/A";
        params.Tonnage = "N/A";
        params.OhpRate = "N/A";
        params.RunnerType = "N/A";
      }
      else{
        if (oldCostParams.cycleTime != costParams.cycleTime) {
          params.CycleTime = costParams.cycleTime
        }else{params.CycleTime = "N/A";}

        if (oldCostParams.machineTonnage != costParams.machineTonnage) {
          params.Tonnage = costParams.machineTonnage
        }else{params.Tonnage = "N/A";}

        if (oldCostParams.ohpRate != costParams.ohpRate) {
          params.OhpRate = costParams.ohpRate
        }else{params.OhpRate = "N/A";}

        if (oldCostParams.runnerType != costParams.runnerType) {
          params.RunnerType = costParams.runnerType
        }else{params.RunnerType = "N/A";}

      };
      // params.CycleTime = costParams.cycleTime || "N/A";
      // params.Tonnage = costParams.machineTonnage || "N/A";
      // params.OhpRate = costParams.ohpRate || "N/A";
      // params.RunnerType = costParams.runnerType || "N/A";
      
      // if(playBookData ) {};

      console.log("🚀 Calling /api/mech/casting/cost-analysis with:", productData);

      const response = await apiFetch("/api/mech/casting/cost-analysis", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        let errorMessage = "Failed to generate quick cost";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          if (errorData.missing_fields) {
            console.error("Missing fields:", errorData.missing_fields);
          }
        } catch (e) {
          console.error("Error parsing error response:", e);
          errorMessage = `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log("✅ Cost Analysis complete:", result);
      
      const playbook = result.Playbook_Data || null;
      const shouldCostResult = result.ShouldCost_Result || null;
      const insights = result.keyInsights || null;
      setKeyInsightsData(insights);

      const assumptionsArray = [];
      
      if (shouldCostResult.MaterialPrice) {
          assumptionsArray.push(`Material Price: $${shouldCostResult.MaterialPrice}/kg `);
      }

      if (shouldCostResult.MHR) {
        if(shouldCostResult.MHRSource == 'PERL'){
          assumptionsArray.push(`Machine rate (${shouldCostResult.MHR_MachineTonnage}  ton machine): $${shouldCostResult.MHR}/hour 🟣`);
          // assumptionsArray.push(`Machine rate: $${shouldCostResult.MHR}/hour 🟣`);
          }else{assumptionsArray.push(`Machine rate: $${shouldCostResult.MHR}/hour `);}
      }

      if (shouldCostResult.LHR) {
        if(shouldCostResult.LHRSource == 'PERL'){
          assumptionsArray.push(`Labour Cost: $${shouldCostResult.LHR}/hour 🟣`);
        }else{assumptionsArray.push(`Labour Cost: $${shouldCostResult.LHR}/hour`);}
      }

      assumptionsArray.push(`Cavity: ${playbook.cavities}`);
      assumptionsArray.push(`Tonnage: ${playbook.machineTonnage} tons`);
      assumptionsArray.push(`Cycle Time: ${playbook.cycleTime}`);
      // assumptionsArray.push(`Runner Type: ${playbook.runnerType}`);

      // Transform backend data to match PerlCostTable format
      console.log(shouldCostResult)
      const costData = shouldCostResult
        ? {
            breakdown: {
              material: shouldCostResult.MaterialCost || 0,
              process: shouldCostResult.ProcessCost || 0,
              // packaging: shouldCostResult.PackagingCost || 0,
              ohp: shouldCostResult.OHPCost || 0,
            },
            assumptions :assumptionsArray,
          }
        : null;

      // Extract database calculation results
      const calculationResult = result.calculationResult || {};
      console.log("📊 Database calculation results:", calculationResult);

      // Update state with AI results
      // setRecipeData(recipe);
      setPlayBookData(playbook);
      setOldPlayBookData(playbook);
      setCostData(costData);
      setSectionStarted((prev) => ({ ...prev, playbook: true, results: true }));
      setActiveSection("results");
      // setLastModifiedSection("results");

    } catch (error) {
      console.error("Quick Cost failed:", error);
      alert(`Failed to generate quick cost: ${error.message}`);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }


  }

  // const handleQuickCost_Pe = async () => {
  //   if (!productData) return;

  //   setLoadingMessage("Generating Quick Cost...");
  //   setIsProcessing(true);
  //   setKeyInsightsData(null);

  //   try {
  //     // Call stored procedure for plastics costing
  //     console.log("📞 Calling stored procedure with:", { productData, playBookData });

  //     const spResult = await callStoredProcedure(productData, playBookData);

  //     if (spResult && Array.isArray(spResult) && spResult.length > 0) {
  //       const resultData = spResult[0];
        
  //       // Helper function to format numbers to 4 decimal places
  //       const to4 = (n) => {
  //         if (n === null || n === undefined || n === '') return 0;
  //         if (typeof n === 'string') {
  //           const cleaned = n.replace(/[$,\s]/g, '').trim();
  //           const parsed = parseFloat(cleaned);
  //           return isNaN(parsed) ? 0 : Number(Number(parsed).toFixed(4));
  //         }
  //         return Number(Number(n).toFixed(4));
  //       };
        
  //       // Map stored procedure output to Analysis Report cost breakdown
  //       // Material = RMPrice: 1.18
  //       // Process = MfrCost: 0.1886
  //       // OHP = oHP: 0.0884
  //       const material = to4(resultData.RMPrice || resultData.Material || resultData.material || resultData.MaterialCost || 0);
  //       const process = to4(resultData.MfrCost || resultData.Manufacturing || resultData.manufacturing || resultData.ProcessCost || 0);
  //       const ohp = resultData.oHP !== null && resultData.oHP !== undefined 
  //         ? to4(resultData.oHP) 
  //         : to4(resultData.OHP || resultData.OHPCost || 0);
        
  //       // Calculate total: material + process + ohp
  //       const calculatedTotal = Number(material) + Number(process) + Number(ohp);
  //       const total = to4(calculatedTotal);
        
  //       // Build assumptions array - prioritize PlayBook values if they exist (user-modified), otherwise use stored procedure results
  //       const costParams = playBookData?.costParams || {};
  //       const assumptionsArray = [];
        
  //       // Part Dimensions - from product data
  //       const dimensionData = productData?.partWeightDimensions || {};
  //       if (dimensionData.partLength && dimensionData.partWidth && dimensionData.partHeight && dimensionData.finishWeight) {
  //         assumptionsArray.push(
  //           `Part dimensions: L: ${dimensionData.partLength}mm, W: ${dimensionData.partWidth}mm, H: ${dimensionData.partHeight}mm, Weight: ${dimensionData.finishWeight}g`
  //         );
  //       }
        
  //       // Machine Tonnage - use PlayBook value if available, otherwise use stored procedure result
  //       if (costParams.machineTonnage) {
  //         assumptionsArray.push(`Machine tonnage: ${costParams.machineTonnage}`);
  //       } else if (resultData.Tonnage || resultData.cTonnage) {
  //         assumptionsArray.push(`Machine tonnage: ${resultData.Tonnage || resultData.cTonnage}`);
  //       }
        
  //       // Cavities - use PlayBook value if available, otherwise use stored procedure result
  //       if (costParams.cavities) {
  //         assumptionsArray.push(`Cavities: ${costParams.cavities}`);
  //       } else if (resultData.cCavities || resultData.Cavities) {
  //         assumptionsArray.push(`Cavities: ${resultData.cCavities || resultData.Cavities}`);
  //       }
        
  //       // Runner Type - use PlayBook value if available, otherwise use stored procedure result
  //       if (costParams.runnerType && costParams.runnerType !== "N/A") {
  //         assumptionsArray.push(`Runner type: ${costParams.runnerType}`);
  //       } else if (resultData.RunnerType) {
  //         assumptionsArray.push(`Runner type: ${resultData.RunnerType}`);
  //       }
        
  //       // Cycle Time - use PlayBook value if available, otherwise use stored procedure result
  //       if (costParams.cycleTime) {
  //         assumptionsArray.push(`Cycle time: ${costParams.cycleTime}s`);
  //       } else if (resultData.CycleTime) {
  //         assumptionsArray.push(`Cycle time: ${resultData.CycleTime}s`);
  //       }
        
  //       // Machine Rate - always from stored procedure (not user-modifiable in PlayBook)
  //       if (resultData.MHR) {
  //         const machineRate = typeof resultData.MHR === 'number' ? resultData.MHR : parseFloat(resultData.MHR);
  //         if (!isNaN(machineRate)) {
  //           assumptionsArray.push(`Machine rate: $${machineRate.toFixed(4)}/hour`);
  //         }
  //       }
        
  //       // Material Price - always from stored procedure (not user-modifiable in PlayBook)
  //       if (resultData.RMPrice) {
  //         const materialPrice = typeof resultData.RMPrice === 'number' ? resultData.RMPrice : parseFloat(resultData.RMPrice);
  //         if (!isNaN(materialPrice)) {
  //           assumptionsArray.push(`Material price: $${materialPrice.toFixed(4)}/kg`);
  //         }
  //       }
        
  //       // Labour Cost - always from stored procedure
  //       if (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) {
  //         const labourCost = typeof (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) === 'number' 
  //           ? (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost)
  //           : parseFloat(resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost);
  //         if (!isNaN(labourCost)) {
  //           assumptionsArray.push(`Labour cost: $${labourCost.toFixed(4)}/unit`);
  //         }
  //       }
        
  //       // OHP Rate - use PlayBook value if available, otherwise use stored procedure result
  //       if (costParams.ohpRate !== null && costParams.ohpRate !== undefined && costParams.ohpRate !== 0) {
  //         const ohpRateValue = typeof costParams.ohpRate === 'number' 
  //           ? costParams.ohpRate 
  //           : parseFloat(costParams.ohpRate);
  //         if (!isNaN(ohpRateValue)) {
  //           assumptionsArray.push(`OHP rate: ${ohpRateValue.toFixed(2)}%`);
  //         }
  //       } else if (resultData.oHP !== null && resultData.oHP !== undefined) {
  //         const ohpRate = typeof resultData.oHP === 'number' ? resultData.oHP : parseFloat(resultData.oHP);
  //         if (!isNaN(ohpRate)) {
  //           // Convert decimal to percentage if needed
  //           const ohpPercent = ohpRate > 1 ? ohpRate : ohpRate * 100;
  //           assumptionsArray.push(`OHP rate: ${ohpPercent.toFixed(2)}%`);
  //         }
  //       }
        
  //       // If no assumptions, add default message
  //       if (assumptionsArray.length === 0) {
  //         assumptionsArray.push("Costing calculated using stored procedure QC_SP_Plastics.");
  //       }
        
  //       const baseOhpRatePercent =
  //         material + process > 0 ? (ohp / (material + process)) * 100 : 0;

  //       // Transform stored procedure result to format expected by ResultsSection
  //       const transformedCostData = {
  //         summary: `Should cost analysis for ${resultData.PartName || resultData.partName || "plastic part"} manufactured in ${resultData.MfrCountry || resultData.mfrCountry || "location"}. Total cost: $${total} per unit.`,
  //         breakdown: {
  //           material: material,
  //           process: process,
  //           logistics: 0, // Plastic costing doesn't have logistics
  //           ohp: ohp,
  //           total_should_cost: total,
  //           currency: "USD/unit",
  //         },
  //         line_items: [],
  //         key_insights: [],
  //         assumptions: assumptionsArray, // Pass as array, not string
  //         _source: "stored_procedure",
  //         _originalResult: resultData,
  //         meta: {
  //           baseOhpCost: ohp,
  //           baseOhpRate: baseOhpRatePercent,
  //           currentOhpRate:
  //             costParams.ohpRate !== undefined && costParams.ohpRate !== null
  //               ? costParams.ohpRate
  //               : baseOhpRatePercent,
  //         },
  //       };
        
  //       // If user already modified PlayBook OHP rate, apply client-side adjustment before storing
  //       const currentPlayBookParams = playBookData?.costParams || {};
  //       let finalCostData = transformedCostData;
  //       if (
  //         currentPlayBookParams.ohpRate !== undefined &&
  //         currentPlayBookParams.ohpRate !== null &&
  //         currentPlayBookParams.ohpRate !== 0
  //       ) {
  //         finalCostData = recalculateOHPCost(
  //           currentPlayBookParams.ohpRate,
  //           transformedCostData,
  //           currentPlayBookParams
  //         );
  //       }

  //       // If Machine Tonnage was modified in PlayBook, fetch and update material price
  //       const tonnage = currentPlayBookParams.machineTonnage;
  //       if (tonnage !== undefined && tonnage !== null && tonnage > 0) {
  //         console.log("\n💰 Tonnage detected in PlayBook, fetching new material price...");
  //         console.log("   - Tonnage:", tonnage);
          
  //         try {
  //           const manufacturingCountry = productData.manufacturingCountry || "";
  //           const materialName = productData.materialSelection || null;
  //           const partWeightG = productData.partWeightDimensions?.finishWeight || 0;

  //           if (manufacturingCountry && partWeightG > 0) {
  //             console.log("   - Manufacturing Country:", manufacturingCountry);
  //             console.log("   - Material Name:", materialName);
  //             console.log("   - Part Weight (g):", partWeightG);

  //             const requestPayload = {
  //               tonnage: tonnage,
  //               manufacturingCountry: manufacturingCountry,
  //               materialName: materialName,
  //             };

  //             console.log("   - Fetching material price from API...");
  //             const response = await apiFetch("/api/mech/material-price", {
  //               method: "POST",
  //               headers: { "Content-Type": "application/json" },
  //               body: JSON.stringify(requestPayload),
  //             });

  //             if (response.ok) {
  //               const result = await response.json();
  //               const newMaterialPricePerKg = parseFloat(result.material_price || 0);

  //               console.log("   ✅ New Material Price received:", `$${newMaterialPricePerKg}/kg`);

  //               if (!isNaN(newMaterialPricePerKg) && newMaterialPricePerKg > 0) {
  //                 // Calculate new material cost per unit: (part_weight_g / 1000) * material_price_per_kg
  //                 // const partWeightKg = parseFloat(partWeightG) / 1000;
  //                 // const newMaterialCost = Number((partWeightKg * newMaterialPricePerKg).toFixed(4));
  //                 // Update Material in cost category table with material price per kg directly
  //                 const newMaterialCost = Number(Number(newMaterialPricePerKg).toFixed(4));

  //                 console.log("   - Using material price per kg directly as material cost");
  //                 console.log("   - New Material Cost:", `$${newMaterialCost}/unit`);

  //                 // Update material cost and total
  //                 const process = to4(finalCostData.breakdown.process || 0);
  //                 const ohp = to4(finalCostData.breakdown.ohp || 0);
  //                 const newTotal = Number((newMaterialCost + process + ohp).toFixed(4));

  //                 console.log("   - Process Cost:", `$${process}/unit`);
  //                 console.log("   - OHP Cost:", `$${ohp}/unit`);
  //                 console.log("   - New Total:", `$${newTotal}/unit`);

  //                 // Update assumptions array with new material price
  //                 const updatedAssumptions = finalCostData.assumptions.map((assumption) => {
  //                   if (assumption.includes("Material price:")) {
  //                     return `Material price: $${newMaterialPricePerKg.toFixed(4)}/kg`;
  //                   }
  //                   return assumption;
  //                 });

  //                 // If material price wasn't in assumptions, add it
  //                 const hasMaterialPrice = updatedAssumptions.some(a => a.includes("Material price:"));
  //                 if (!hasMaterialPrice) {
  //                   updatedAssumptions.push(`Material price: $${newMaterialPricePerKg.toFixed(4)}/kg`);
  //                 }

  //                 // Update finalCostData with new material cost
  //                 finalCostData = {
  //                   ...finalCostData,
  //                   breakdown: {
  //                     ...finalCostData.breakdown,
  //                     material: newMaterialCost,
  //                     total_should_cost: newTotal,
  //                   },
  //                   summary: finalCostData.summary.replace(
  //                     /\$[\d.]+ per unit/,
  //                     `$${newTotal} per unit`
  //                   ),
  //                   assumptions: updatedAssumptions,
  //                   meta: {
  //                     ...finalCostData.meta,
  //                     materialPricePerKg: newMaterialPricePerKg,
  //                   },
  //                 };

  //                 console.log("   ✅ Material cost updated in cost breakdown");
  //                 console.log("   ✅ Material price updated in assumptions list");
  //               } else {
  //                 console.warn("   ⚠️ Invalid material price received:", result);
  //               }
  //             } else {
  //               const errorData = await response.json().catch(() => ({ error: "Failed to fetch material price" }));
  //               console.warn("   ⚠️ Failed to fetch material price:", errorData.error || "Unknown error");
  //             }
  //           } else {
  //             console.warn("   ⚠️ Missing required data for material price update:", {
  //               manufacturingCountry: !!manufacturingCountry,
  //               partWeightG: partWeightG > 0,
  //             });
  //           }
  //         } catch (error) {
  //           console.error("   ❌ Error updating material price:", error);
  //           // Don't block the flow - continue with stored procedure result
  //         }
  //       }

  //       // Store the (possibly adjusted) result as costData
  //       setCostData(finalCostData);
        
  //       // Map stored procedure output to PlayBook costParams
  //       // cavities = cCavities: 2
  //       // cycleTime = CycleTime: 39.71
  //       // machineTonnage = Tonnage: 39
  //       // ohpRate = oHP: 0.0669 (convert from decimal to percentage: 0.0669 * 100 = 6.69)
  //       // runnerType = RunnerType: "Cold runner"
        
  //       // Helper function to parse percentage (handles both decimal and percentage formats)
  //       const parsePercentage = (value) => {
  //         if (value === null || value === undefined || value === "") return 0;
  //         if (typeof value === "string") {
  //           // Remove % sign if present
  //           const cleaned = value.replace(/%/g, "").trim();
  //           const num = parseFloat(cleaned);
  //           // If the number is > 1, assume it's already a percentage, otherwise assume it's a decimal
  //           return isNaN(num) ? 0 : (num > 1 ? num : num * 100);
  //         }
  //         if (typeof value === "number") {
  //           // If the number is > 1, assume it's already a percentage, otherwise assume it's a decimal
  //           return value > 1 ? value : value * 100;
  //         }
  //         return 0;
  //       };

  //       // On first run: map stored procedure results to PlayBook
  //       // On subsequent runs: preserve user-modified PlayBook values, only update missing ones
  //       const existingCostParams = playBookData?.costParams || {};
  //       const updatedCostParams = {
  //         // Preserve user-modified values, or use stored procedure results if not modified
  //         cavities: existingCostParams.cavities !== undefined && existingCostParams.cavities !== null
  //           ? existingCostParams.cavities
  //           : parseInt(resultData.cCavities || resultData.Cavities || resultData.cavities || 0),
  //         cycleTime: existingCostParams.cycleTime !== undefined && existingCostParams.cycleTime !== null
  //           ? existingCostParams.cycleTime
  //           : parseFloat(resultData.CycleTime || resultData.cycleTime || 0),
  //         machineTonnage: existingCostParams.machineTonnage !== undefined && existingCostParams.machineTonnage !== null
  //           ? existingCostParams.machineTonnage
  //           : parseInt(resultData.Tonnage || resultData.cTonnage || resultData.machineTonnage || 0),
  //         ohpRate: existingCostParams.ohpRate !== undefined && existingCostParams.ohpRate !== null && existingCostParams.ohpRate !== 0
  //           ? existingCostParams.ohpRate
  //           : (resultData.oHP !== null && resultData.oHP !== undefined 
  //               ? parsePercentage(resultData.oHP)
  //               : 0),
  //         runnerType: existingCostParams.runnerType !== undefined && existingCostParams.runnerType !== null && existingCostParams.runnerType !== "N/A"
  //           ? existingCostParams.runnerType
  //           : (resultData.RunnerType || resultData.runnerType || "N/A"),
  //       };

  //       // Update playBookData with mapped costParams (preserves user modifications)
  //       const mergedPlayBookState = {
  //         ...(playBookData || {}),
  //         costParams: updatedCostParams,
  //       };
  //       setPlayBookData(mergedPlayBookState);

  //       // Navigate to results section
  //       setActiveSection("results");
  //       setSectionStarted((prev) => ({ ...prev, playbook: true, results: true }));

  //       await fetchKeyInsights(productData, mergedPlayBookState);
  //     } else {
  //       throw new Error("Stored procedure returned no data");
  //     }
  //   } catch (error) {
  //     console.error("Quick Cost failed:", error);
  //     alert(`Failed to calculate cost: ${error.message}`);
  //   } finally {
  //     setLoadingMessage("");
  //     setIsProcessing(false);
  //   }
  // };

  const navItems = [
    {
      id: "product",
      label: "Product Information",
      icon: <DescriptionIcon />,
      disabled: false,
      title: "Build Product Profile",
      description:
        "Answer a few quick questions about your product to generate an AI-driven cost analysis with  manufacturing parameters, and detailed insights.",
    },
    // {
    //   id: "formulation",
    //   label: "Formulation",
    //   icon: <ScienceIcon />,
    //   disabled: false,
    //   title: "Ready to Build Formulation",
    //   description: "View and edit ingredient composition with functional groups and percentages.",
    // },
    {
      id: "playbook",
      label: "PlayBook",
      icon: <TuneIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Ready to Configure PlayBook",
      description:
        "Configure manufacturing parameters including moisture content, process loss, packaging loss, overhead percentages, and packaging details.",
    },
    {
      id: "results",
      label: "Analysis Report",
      icon: <AssessmentIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Analysis Report",
      description:
        "Access detailed cost breakdown, risk analysis, and substitute recommendations with visual charts and insights.",
    },
  ];

const costRows = useMemo(() => buildCostRows(costData?.breakdown || {}), [costData]);
 
  const costTotal = useMemo(() => {
    const declaredTotal =
      costData?.breakdown?.total_should_cost ?? costData?.breakdown?.total;
    const declaredNumber = Number(declaredTotal);
    if (Number.isFinite(declaredNumber)) return declaredNumber;
    console.log(costRows)
    return costRows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
  }, [costData, costRows]);


  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName="Mechanical"
          industry="Mechanical"
          commodity="Casting"
          tierImage="/images/Silver.jpg"
          tierLabel="Agentic Lite"
        />

        <Box
          sx={{
            display: "flex",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <LeftNavigation
            navItems={navItems}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            showTooltips={true}
          />

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: perlSpacing.mainPadding || 3,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: perlColors.border,
                borderRadius: "4px",
              },
            }}
          >
            {/* Show info notification only for product section when not started */}
            {!sectionStarted.product && activeSection === "product" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {/* Keep CookiesQuestions mounted but hidden when not active to preserve state */}
            {sectionStarted.product && (
              <Box sx={{ display: activeSection === "product" ? "block" : "none", height:"92%"}}>
                <CastingQuestions
                  onComplete={handleProductComplete}
                  onDataChange={setProductData}
                  onDemoModeChange={handleDemoModeChange}
                  onReset={handleProductReset}
                  initialData={productData}
                />
              </Box>
            )}

            {activeSection === "formulation" && sectionStarted.formulation && (
              <Box>
                <FormulationTable
                  data={recipeData}
                  onRowChange={(index, field, value) => {
                    const updated = [...recipeData];
                    updated[index][field] = value;
                    setRecipeData(updated);
                  }}
                  onRowAdd={() => {
                    setRecipeData([
                      ...recipeData,
                      { ingredient: "", percentage: 0, functional_group: null },
                    ]);
                  }}
                  onRowDelete={(index) => {
                    setRecipeData(recipeData.filter((_, i) => i !== index));
                  }}
                  functionalGroups={functionalGroups}
                  brandColor="#ff6034"
                  editable={true}
                  showTotal={true}
                />
              </Box>
            )}

            {activeSection === "playbook" && sectionStarted.playbook && (
              <Box>
                {playBookData ? (
                  <CastingPlayBook data={playBookData} onChange={handlePlayBookChange} />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Generate Quick cost first to load PlayBook data
                  </Typography>
                )}
              </Box>
            )}

            {activeSection === "results" && sectionStarted.results && (
              <Box>
                {costData ? (
                  <ResultsSection
                    costData={costData}
                    costRows={costRows}
                    costTotal={costTotal}
                    riskData={riskData}
                    substituteData={substituteData}
                    productImageUrl={productImageUrl}
                    generatingImage={generatingImage}
                    onRegenerateImage={generateProductImage}
                    productName={productData?.productName}
                    productData={productData}
                    brandColor="#ff6034"
                    substituteSectionTitle="Casting Recommendations"
                    assumptions={costData?.assumptions || []}
                    keyInsights={keyInsightsData}
                    onNavigateToPlayBook={() => {
                      setSectionStarted((prev) => ({ ...prev, playbook: true }));
                      setActiveSection("playbook");
                    }}
                    renderedAssumptions={renderAssumptions}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Run Quick Cost to generate analysis report
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Quick Cost Button */}
        {sectionStarted.product &&  (
          <QuickCostButton
            onClick={handleQuickCost}
            disabled={!productData}
            loading={isProcessing}
            brandColor="#ff6034"
          />
        )}
        
        {/* Progressive Analysis Tracker Dialog */}
        <Dialog
          open={showProgressTracker}
          maxWidth={false}
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              borderRadius: 4,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <ProgressiveAnalysisTracker
              steps={progressSteps}
              currentStepNumber={currentProgressStep}
              overallProgress={overallProgress}
            />
          </DialogContent>
        </Dialog>

        {/* Legacy Circular Loading (kept for backward compatibility) */}

        <CircularLoading
          open={isProcessing && !showProgressTracker}
          message={loadingMessage || "Running quick cost..."}
          subtext={productData?.productName ? `Product: ${productData.productName}` : undefined}
        />
      </Box>
    </SidebarLayout>
  );
}
